import React, { useState } from 'react';
import {
    Box,
    Paper,
    Stack,
    Tooltip,
    Typography,
    IconButton,
    Skeleton,
    Alert,
    Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RosterList from './RosterList';
import AddRosterDialog from './AddRosterDialog';
import { useRosterManagement } from '../hooks/instructorHooks';
import { getStudentModel, getUserByEmail } from '../utils/instructorUtils';

const POLLING_INTERVAL = 15000; // 15 seconds

const SidebarHeader = ({
                           isCollapsed,
                           setIsCollapsed,
                           setIsAddingRoster
                       }) => (
    <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: 1,
        borderColor: 'divider',
        minWidth: isCollapsed ? '60px' : 'auto'
    }}>
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
                display: isCollapsed ? 'none' : 'flex',
                width: '100%'
            }}
        >
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Class Rosters
            </Typography>
            <Tooltip title="Add Roster">
                <IconButton
                    onClick={() => setIsAddingRoster(true)}
                    color="primary"
                    size="small"
                >
                    <AddIcon />
                </IconButton>
            </Tooltip>
        </Stack>
        <IconButton
            onClick={() => setIsCollapsed(!isCollapsed)}
            size="small"
            sx={{
                ml: isCollapsed ? 'auto' : 0,
                mr: isCollapsed ? 'auto' : 0
            }}
        >
            {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
    </Box>
);

const LoadingState = () => (
    <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={100} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={100} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={100} />
    </Box>
);



const InstructorSidebar = ({ currentModel, onModelSelect }) => {
    // State
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isAddingRoster, setIsAddingRoster] = useState(false);
    const [newRosterName, setNewRosterName] = useState('');
    const [expandedRoster, setExpandedRoster] = useState(null);
    const [selectedMEL, setSelectedMEL] = useState(null);
    const [error, setError] = useState(null);

    // Custom hook for roster management with new pull-based approach
    const {
        rosters,
        studentStatuses,
        activeUsers,
        registeredUsers,
        fileInputKey,
        isLoading,
        lastUpdate,
        handleFileUpload,
        handleAddRoster,
        handleExportSummary,
        handleDeleteRoster,
        refreshData,
        userEmailToId
    } = useRosterManagement(currentModel, POLLING_INTERVAL);

    const handleMELSelect = async (email) => {
        const isDeselecting = selectedMEL === email;
        setSelectedMEL(isDeselecting ? null : email);

        if (isDeselecting) {
            onModelSelect({
                modelName: currentModel,
                modelData: null,
                studentEmail: null
            });
            return;
        }

        try {
            const userData = await getUserByEmail(email);
            if (!userData) {
                setError(`Could not find user data for ${email}`);
                return;
            }

            const modelData = await getStudentModel(userData.uid, currentModel);
            if (modelData) {
                onModelSelect({
                    modelName: currentModel,
                    modelData: modelData,
                    studentEmail: email
                });
            }
        } catch (error) {
            console.error('Error loading student model:', error);
            setError('Failed to load student model');
            setSelectedMEL(null);
        }
    };

    const handleAddRosterClick = async () => {
        try {
            if (await handleAddRoster(newRosterName)) {
                setNewRosterName('');
                setIsAddingRoster(false);
            }
        } catch (error) {
            setError('Failed to add roster');
        }
    };

    const handleDeleteRosterClick = async (rosterId) => {
        try {
            if (window.confirm('Are you sure you want to delete this roster?')) {
                await handleDeleteRoster(rosterId);
                setExpandedRoster(null);
                setSelectedMEL(null);
            }
        } catch (error) {
            setError('Failed to delete roster');
        }
    };

    const handleRefresh = () => {
        refreshData();
        setError(null); // Clear any existing errors
    };

    return (
        <Box
            sx={{
                width: isCollapsed ? 60 : 400,
                height: 'calc(100vh - 60px)',
                borderRight: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                overflow: 'hidden',
                transition: 'width 0.2s ease-in-out',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <SidebarHeader
                isCollapsed={isCollapsed}
                setIsCollapsed={setIsCollapsed}
                setIsAddingRoster={setIsAddingRoster}
                isLoading={isLoading}
                lastUpdate={lastUpdate}
                onRefresh={handleRefresh}
            />

            <Box sx={{
                flex: 1,
                overflow: 'auto',
                visibility: isCollapsed ? 'hidden' : 'visible',
                transition: 'visibility 0.2s'
            }}>
                {isLoading && !rosters.length ? (
                    <LoadingState />
                ) : (
                    <Paper elevation={0} sx={{ p: 2 }}>
                        <RosterList
                            rosters={rosters}
                            expandedRoster={expandedRoster}
                            setExpandedRoster={setExpandedRoster}
                            handleFileUpload={handleFileUpload}
                            handleDeleteRoster={handleDeleteRosterClick}
                            handleExportSummary={handleExportSummary}
                            handleMELSelect={handleMELSelect}
                            studentStatuses={studentStatuses}
                            activeUsers={activeUsers}
                            registeredUsers={registeredUsers}
                            selectedMEL={selectedMEL}
                            fileInputKey={fileInputKey}
                            currentModel={currentModel}
                            userEmailToId={userEmailToId}
                        />
                    </Paper>
                )}
            </Box>

            <AddRosterDialog
                isOpen={isAddingRoster}
                onClose={() => setIsAddingRoster(false)}
                newRosterName={newRosterName}
                setNewRosterName={setNewRosterName}
                handleAddRoster={handleAddRosterClick}
            />

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
            >
                <Alert
                    onClose={() => setError(null)}
                    severity="error"
                    sx={{ width: '100%' }}
                >
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default InstructorSidebar;